import { ComponentType } from "react";
import { ColumnDefinition } from "src/components/Common/CustomizeColumns/types";
import KeywordsTable from "./KeywordsTable";
import CreativeTable from "./CreativeTable";

/** Context the parent passes down so each tab can build its own request params. */
export interface TabRequestContext {
  orderId:           string;
  startDate:         string;
  endDate:           string;
  dataBy:            string;
  selectedLineItems: string[];
}

/** Props every tab table component receives from the parent. */
export interface TabComponentProps {
  rows:           Record<string, unknown>[];
  isLoading:      boolean;
  currentPage:    number;
  totalPages:     number;
  totalRecords:   number;
  perPage:        number;
  sortColumn:     string;
  sortDirection:  "asc" | "desc";
  exchangeId:     string | number;
  onSortChange:   (columnId: string) => void;
  onPageChange:   (nextPage: number) => void;
  onPerPageChange: (nextPerPage: number) => void;
  /** Ordered list of column IDs to display. undefined = show all. */
  visibleColumns?: string[];
}

export interface TabDefinition {
  /** Stable id used in the cache key and as the React key. */
  tabId:             string;
  /** Human label shown in the tab bar. */
  tabLabel:          string;
  /** Table component. `null` means "render the page's primary/main table". */
  tableComponent:    ComponentType<TabComponentProps> | null;
  /** API endpoint. `null` for the main tab (it uses the page's existing flow). */
  endpoint:          string | null;
  /** Default sort column for this tab. */
  defaultSortColumn: string;
  /** Default sort direction for this tab. */
  defaultSortDirection: "asc" | "desc";
  /**
   * Builds the *query-identity* params for this tab — everything that, when changed,
   * means the cached data is stale. Page/perPage/sort are added by the hook.
   */
  buildRequestParams: (context: TabRequestContext) => Record<string, unknown>;
  /** Controls tab visibility per exchange. Omit = always visible. */
  showForExchange?: (exchangeData: { id: number; exchanges: { exchange_id: number }[] }) => boolean;
  /** Columns always visible, pinned first, NOT in Customize Columns dropdown. */
  requiredColumns?: ColumnDefinition[];
  /** Display label overrides for permitted metrics. Falls back to formatMetricName if absent. */
  metricLabels?: Record<string, string>;
  /** All available metric columns for this tab (used in user permission settings). */
  availableMetrics?: { id: string; label: string }[];
  /** Metric IDs selected by default on first load. Omit = all availableMetrics. Empty array = tab disabled by default. */
  defaultSelectedMetrics?: string[];
  /** If true, tab is export-only — shown in user permissions and export settings but not in the Reports tab bar. */
  exportOnly?: boolean;
}

export const tabRegistry: TabDefinition[] = [
  {
    tabId:                "main",
    tabLabel:             "Line Item Data",
    tableComponent:       null,
    endpoint:             null,
    defaultSortColumn:    "start_date",
    defaultSortDirection: "desc",
    buildRequestParams:   () => ({}),
  },
  {
    tabId:                "creative",
    tabLabel:             "Creative Data",
    tableComponent:       CreativeTable,
    endpoint:             "/exchange-creative-data",
    defaultSortColumn:    "impressions",
    defaultSortDirection: "desc",
    buildRequestParams:   (ctx) => ({
      order_id:   ctx.orderId,
      line_items: ctx.selectedLineItems,
      start_date: ctx.startDate,
      end_date:   ctx.endDate,
      data_by:    ctx.dataBy,
    }),
    requiredColumns: [
      { id: "line_item_name", label: "Line Item" },
      { id: "creative_name",  label: "Creative" },
      { id: "start_date",     label: "Start Date" },
      { id: "end_date",       label: "End Date" },
    ],
    metricLabels: {
      ctr:             "CTR",
      completion_rate: "Completion Rate",
      estimate_spend:  "Estimated Spend",
      completed_views: "Completed Views",
    },
    availableMetrics: [
      { id: "impressions",     label: "Impressions" },
      { id: "clicks",          label: "Clicks" },
      { id: "conversions",     label: "Conversions" },
      { id: "completed_views", label: "Completed Views" },
      { id: "estimate_spend",  label: "Estimated Spend" },
      { id: "ctr",             label: "CTR" },
      { id: "completion_rate", label: "Completion Rate" },
    ],
    defaultSelectedMetrics: ["impressions", "clicks", "ctr"],
  },
  {
    tabId:                "keywords",
    tabLabel:             "Keyword Data",
    tableComponent:       KeywordsTable,
    endpoint:             "/keyword-data",
    defaultSortColumn:    "impressions",
    defaultSortDirection: "desc",
    showForExchange:      (ex) => ex.exchanges.some(e => e.exchange_id === 4),
    buildRequestParams:   (ctx) => ({
      line_items: ctx.selectedLineItems,
      start_date: ctx.startDate,
      end_date:   ctx.endDate,
      data_by:    ctx.dataBy,
    }),
    requiredColumns: [
      { id: "line_item_name", label: "Line Item" },
      { id: "keyword_text",   label: "Keyword" },
      { id: "start_date",     label: "Start Date" },
      { id: "end_date",       label: "End Date" },
    ],
    metricLabels: {
      calc_ctr:     "CTR",
      calc_avg_cpc: "CPC",
    },
    availableMetrics: [
      { id: "impressions",   label: "Impressions" },
      { id: "clicks",        label: "Clicks" },
      { id: "conversions",   label: "Conversions" },
      { id: "cost",          label: "Cost" },
      { id: "calc_ctr",      label: "CTR" },
      { id: "calc_avg_cpc",  label: "CPC" },
    ],
  },
  {
    tabId:                "databy",
    tabLabel:             "Data By Period",
    tableComponent:       null,
    endpoint:             null,
    defaultSortColumn:    "start_date",
    defaultSortDirection: "desc",
    buildRequestParams:   () => ({}),
    exportOnly:           true,
    availableMetrics: [
      { id: "impressions",     label: "Impressions" },
      { id: "clicks",          label: "Clicks" },
      { id: "ctr",             label: "CTR" },
      { id: "completed_views", label: "Completed Views" },
      { id: "completion_rate", label: "Completion Rate" },
      { id: "conversions",     label: "Conversions" },
      { id: "estimate_spend",  label: "Estimated Spend" },
    ],
    defaultSelectedMetrics: ["impressions", "clicks", "ctr"],
  },
];
